/** CSV template picker — portaled menu; ignores scroll events that originate inside the menu. */
import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Trash2 } from 'lucide-react';

import { PresetColorSwatch } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { CsvExportTemplate } from '../useCsvExportTemplates';

interface CsvTemplateMenuProps {
  templates: CsvExportTemplate[];
  activeTemplateId: string | null;
  disabled: boolean;
  showDelete: boolean;
  onApply: (template: CsvExportTemplate) => void;
  onDelete: (template: CsvExportTemplate) => boolean;
}

export const CsvTemplateMenu: FC<CsvTemplateMenuProps> = ({
  templates,
  activeTemplateId,
  disabled,
  showDelete,
  onApply,
  onDelete,
}) => {
  const [open, setOpen] = useState(false);
  const [menuBox, setMenuBox] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updateMenuBox = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuBox({ top: r.bottom + 4, left: r.left, width: r.width });
  }, []);

  useEffect(() => {
    if (!open) return;
    updateMenuBox();
    const onScrollCapture = (e: Event) => {
      const t = e.target as Node | null;
      if (t && menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('resize', updateMenuBox);
    window.addEventListener('scroll', onScrollCapture, true);
    return () => {
      window.removeEventListener('resize', updateMenuBox);
      window.removeEventListener('scroll', onScrollCapture, true);
    };
  }, [open, updateMenuBox]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (templates.length === 0) return null;

  const active = templates.find((t) => t.id === activeTemplateId) ?? null;

  const menu =
    open &&
    createPortal(
      <div
        ref={menuRef}
        className="fixed z-[260] max-h-52 min-h-0 min-w-[12rem] touch-pan-y overflow-y-auto overscroll-contain rounded-md border bg-popover p-1 shadow-lg"
        style={{ top: menuBox.top, left: menuBox.left, width: Math.max(menuBox.width, 200) }}
        role="listbox"
        aria-label="CSV templates"
      >
        {templates.map((t) => (
          <div
            key={t.id}
            className={cn(
              'group flex items-center gap-1 rounded-md px-1 py-0.5',
              t.id === activeTemplateId ? 'bg-primary/5' : 'hover:bg-muted/50',
            )}
          >
            <button
              type="button"
              role="option"
              className="flex min-w-0 flex-1 items-center gap-2 rounded px-1 py-1.5 text-left"
              onClick={() => {
                onApply(t);
                setOpen(false);
              }}
            >
              <PresetColorSwatch color={t.color} />
              <span className="truncate text-xs font-medium">
                {t.name}
                {t.isDefault ? ' (default)' : ''}
              </span>
            </button>
            {showDelete ? (
              <button
                type="button"
                className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                aria-label={`Delete template ${t.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onDelete(t)) setOpen(false);
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        ))}
      </div>,
      document.body,
    );

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex w-full min-w-0 items-center gap-2 rounded-md border px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent',
          active ? 'border-primary/30 bg-primary/5 text-primary' : 'border-border/60 bg-background',
          disabled && 'pointer-events-none opacity-50',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {active ? <PresetColorSwatch color={active.color} /> : null}
        <span className="min-w-0 flex-1 truncate font-medium">
          {active ? active.name : 'Choose template'}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
      </button>
      {menu}
    </div>
  );
};
